import { Children, Fragment, isValidElement, useId, useState, type ReactNode, type SelectHTMLAttributes } from "react";
import Select, { components, type OptionProps, type MultiValue } from "react-select";
import { useLocale } from "../../i18n";
import { useControlHost } from "./useControlHost";
import { SelectMenuPortal } from "./SelectMenuPortal";
import { useOutsideDismiss } from "../useOutsideDismiss";

type Option = { value: string; label: string; isDisabled?: boolean };
type SharedProps = Pick<SelectHTMLAttributes<HTMLSelectElement>, "id" | "name" | "title" | "required" | "disabled" | "aria-label" | "aria-labelledby" | "aria-describedby" | "aria-invalid"> & { children: ReactNode; inlineMenu?: boolean; onSearch?: (value: string) => void; loading?: boolean };
type Props = SharedProps & (
  { multiple?: false; value: string; onChange: (value: string) => void } |
  { multiple: true; value: readonly string[]; onChange: (value: string[]) => void }
);

function text(node: ReactNode): string {
  return Children.toArray(node).map(child => isValidElement<{ children?: ReactNode }>(child) ? text(child.props.children) : String(child)).join("");
}
function optionsFromChildren(children: ReactNode): Option[] {
  return Children.toArray(children).flatMap(child => {
    if (!isValidElement<{ value?: string; children?: ReactNode; disabled?: boolean }>(child)) return [];
    if (child.type === Fragment) return optionsFromChildren(child.props.children);
    return child.type === "option" ? [{ value: String(child.props.value ?? text(child.props.children)), label: text(child.props.children), isDisabled: child.props.disabled }] : [];
  });
}
function SelectOption(props: OptionProps<Option, boolean>) {
  return <components.Option {...props} innerProps={{ ...props.innerProps, ...{ "data-option-value": props.data.value } }} />;
}

/** Option children preserve each screen's API identifiers and translated labels. */
export function AppSelect(props: Props) {
  const { lang, w } = useLocale();
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const host = useControlHost(props.disabled);
  const [menuOpen, setMenuOpen] = useState(false);
  useOutsideDismiss(host.ref, menuOpen, () => setMenuOpen(false), id);
  const options = optionsFromChildren(props.children);
  const find = (value: string) => options.find(option => option.value === value) ?? { value, label: value };
  const selected = props.multiple ? props.value.map(find) : props.value === "" ? null : find(props.value);
  const empty = options.find(option => option.value === "");
  return <div ref={host.ref} className="mzn-select" title={props.title} data-field-required={props.required || undefined} data-value={props.multiple ? JSON.stringify(props.value) : props.value}>
    <Select<Option, boolean> inputId={id} instanceId={id} name={props.name} classNamePrefix="mzn-select"
      options={options} value={selected} isMulti={!!props.multiple} isDisabled={host.disabled}
      isLoading={props.loading} filterOption={props.onSearch ? null : undefined}
      onInputChange={(value, meta) => { if (meta.action === "input-change" && !host.isLocked()) props.onSearch?.(value); }}
      required={props.required} isRtl={lang === "ar"} isSearchable isClearable={props.multiple || (!props.required && empty !== undefined)}
      aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]}
      aria-describedby={props["aria-describedby"]} aria-invalid={props["aria-invalid"]}
      placeholder={empty?.label ?? w("اختر…", "Select…")} noOptionsMessage={() => w("لا توجد خيارات مطابقة", "No matching options")}
      loadingMessage={() => w("جارٍ التحميل…", "Loading…")}
      menuPortalTarget={props.inlineMenu ? undefined : host.portal} menuPosition={props.inlineMenu ? "absolute" : "fixed"} menuPlacement="auto" maxMenuHeight={260}
      closeMenuOnSelect={!props.multiple} components={{ Option: SelectOption, MenuPortal: SelectMenuPortal }}
      menuIsOpen={menuOpen} onMenuOpen={() => setMenuOpen(true)} onMenuClose={() => setMenuOpen(false)}
      onKeyDown={event => {
        const dialog = host.ref.current?.closest("dialog");
        if (event.key === "Escape" && !menuOpen && dialog) {
          event.preventDefault();
          dialog.requestClose();
        }
      }}
      ariaLiveMessages={{
        guidance: ({ context }) => context === "menu" ? w("استخدم الأسهم للتنقل، وإدخال للاختيار، وهروب للإغلاق.", "Use arrows to navigate, Enter to select, Escape to close.") : w("اكتب للبحث أو اضغط السهم لفتح الخيارات.", "Type to search or press Down to open options."),
        onChange: ({ label, action }) => action === "clear" ? w("تم مسح الاختيار", "Selection cleared") : `${label ?? ""} ${w("تم تحديث الاختيار", "Selection updated")}`,
        onFocus: ({ label }) => label,
        onFilter: ({ resultsMessage }) => resultsMessage,
      }}
      screenReaderStatus={({ count }) => w(`${count} خيارات متاحة`, `${count} options available`)}
      styles={{ menuPortal: base => ({ ...base, zIndex: 1000 }), container: base => ({ ...base, minWidth: 0 }) }}
      onChange={selection => {
        if (host.isLocked()) return;
        if (props.multiple) props.onChange((selection as MultiValue<Option>).map(item => item.value).filter(Boolean));
        else props.onChange((selection as Option | null)?.value ?? "");
      }} />
  </div>;
}
